import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { useI18n } from '@/hooks/useI18n';
import { Palette, User, Globe, Award, Languages, Camera } from 'lucide-react';

interface BrandPageSkeletonProps {
  className?: string;
}

const SkeletonInput: React.FC<{ h?: string }> = ({ h = 'h-10' }) => (
  <div className={`${h} bg-gray-200 rounded animate-pulse w-full`} />
);

export const BrandPageSkeleton: React.FC<BrandPageSkeletonProps> = ({ className }) => {
  const { t } = useI18n();

  return (
    <div className={`max-w-6xl mx-auto pb-4 space-y-6 ${className ?? ''}`} aria-hidden="true">

      {/* Row 1: Professional info | Gallery + Logo */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">

        {/* Professional info */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-xl font-normal flex items-center gap-2">
              <User className="h-4 w-4" />
              {t('brand.generalInfo.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{t('brand.generalInfo.location')}</Label>
              <SkeletonInput />
            </div>
            <div className="space-y-2">
              <Label>{t('brand.generalInfo.yearsExperience')}</Label>
              <SkeletonInput />
            </div>
            <div className="space-y-2">
              <Label>{t('brand.destinations.title')}</Label>
              <SkeletonInput />
            </div>
            <div className="flex flex-wrap gap-2">
              {[60, 80, 70].map((w, i) => (
                <div key={i} className="h-6 bg-gray-200 rounded-full animate-pulse" style={{ width: w }} />
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col gap-6 md:col-span-3">
          {/* Gallery */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-normal flex items-center gap-2">
                <Camera className="h-4 w-4" />
                {t('brand.gallery.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-3">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="h-[90px] bg-gray-200 rounded animate-pulse" />
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Logo */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-normal flex items-center gap-2">
                <Palette className="h-4 w-4" />
                {t('brand.logo.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-row items-center justify-around gap-6">
                <div className="flex flex-col items-center gap-4">
                  <p className="text-sm font-medium">{t('brand.logo.title')}</p>
                  <div className="h-[90px] w-[180px] bg-gray-200 rounded animate-pulse" />
                  <p className="text-xs text-gray-500 text-center max-w-[180px]">{t('brand.logo.extendedDescription')}</p>
                </div>
                <div className="flex flex-col items-center gap-4">
                  <p className="text-sm font-medium">{t('brand.logo.titleCompact')}</p>
                  <div className="h-[90px] w-[90px] bg-gray-200 rounded animate-pulse" />
                  <p className="text-xs text-gray-500 text-center max-w-[180px]">{t('brand.logo.compactDescription')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Bio */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-normal flex items-center gap-2">
            <User className="h-4 w-4" />
            {t('brand.presentation.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>{t('brand.presentation.headline')}</Label>
            <SkeletonInput />
            <p className="text-xs text-gray-500">{t('brand.presentation.headlineHelper')}</p>
          </div>
          <div className="space-y-2">
            <Label>{t('brand.presentation.bio')}</Label>
            <SkeletonInput h="h-[120px]" />
            <p className="text-xs text-gray-500">{t('brand.presentation.bioHelper')}</p>
          </div>
        </CardContent>
      </Card>

      {/* Row 3: Social | Languages + Certifications */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">

        {/* Social links */}
        <Card className="md:col-span-3">
          <CardHeader>
            <CardTitle className="text-xl font-normal flex items-center gap-2">
              <Globe className="h-4 w-4" />
              {t('brand.social.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(['instagram', 'linkedin', 'website', 'facebook'] as const).map((key) => (
                <div key={key} className="space-y-2">
                  <Label>{t(`brand.social.${key}`)}</Label>
                  <SkeletonInput />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Languages + Certifications */}
        <div className="flex flex-col gap-6 md:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-normal flex items-center gap-2">
                <Languages className="h-4 w-4" />
                {t('brand.languages.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <SkeletonInput />
                <div className="h-10 bg-gray-200 rounded animate-pulse w-20 shrink-0" />
              </div>
              <div className="flex flex-wrap gap-2">
                {[50, 70, 60].map((w, i) => (
                  <div key={i} className="h-6 bg-gray-200 rounded-full animate-pulse" style={{ width: w }} />
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-normal flex items-center gap-2">
                <Award className="h-4 w-4" />
                {t('brand.certifications.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <SkeletonInput />
                <div className="h-10 bg-gray-200 rounded animate-pulse w-20 shrink-0" />
              </div>
              <div className="flex flex-wrap gap-2">
                {[60, 50, 75].map((w, i) => (
                  <div key={i} className="h-6 bg-gray-200 rounded-full animate-pulse" style={{ width: w }} />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <div className="h-11 bg-gray-200 rounded animate-pulse w-36" />
      </div>
    </div>
  );
};
